import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import {
  Stack,
  Group,
  Button,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Switch,
  NumberInput,
  Paper,
  Text,
  Tabs,
  ActionIcon,
  Loader,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useTabActionsOptional } from "../../context/tabContext";
import { buildTabKey } from "../../utils/tabNavigation";
import { requestNotificationInboxRefresh } from "../../hooks/useNotificationInbox";
import RemoteSelect2 from "../../components/remoteSelect2";
import McaNotificationTemplateInput from "../../components/mca/McaNotificationTemplateInput";
import NotificationWatchFieldsEditor from "../../components/notification/NotificationWatchFieldsEditor";
import {
  NOTIFICATION_ENTITIES,
  NOTIFICATION_EVENTS,
  NOTIFICATION_CHANNELS,
  RECIPIENT_TYPES,
  DOCUMENT_ROLES,
  defaultNotificationRule,
  getVariablesForEntity,
  variableLabel,
} from "../../data/notificationEntities";

const apiEntity = "notificationRule";

const formatUserLabel = (data) =>
  `[${data?.staffId ?? "--"}] ${data?.name ?? ""} ${data?.cname ?? ""}`.trim();

const entityLabel = (value) =>
  NOTIFICATION_ENTITIES.find((e) => e.value === value)?.label ?? value;

const toRefId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value._id ?? null;
};

const getSaveErrorMessage = (api, res) => {
  if (typeof res?.error === "string") return res.error;
  if (res?.error != null && typeof res.error === "object") {
    const fromError = api.getErrorMsg(res.error);
    if (fromError) return fromError;
  }
  return res?.message || "Request failed";
};

const notify = ({ title, message, color }) => {
  showNotification({ title, message, color });
};

const NotificationRuleEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tabNav = useTabNavigate();
  const tabActions = useTabActionsOptional();
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const isNew = !id || id === "new";
  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [rule, setRule] = useState(defaultNotificationRule());

  const targetEntity = rule.triggerConfig?.targetEntity;
  const variables = getVariablesForEntity(targetEntity);
  const templateVariables = useMemo(
    () =>
      variables.map((name) => ({
        value: name,
        label: variableLabel(name),
        token: `{{${name}}}`,
      })),
    [variables]
  );
  const docRoles = targetEntity ? (DOCUMENT_ROLES[targetEntity] ?? []) : [];

  const syncTabLabel = (name) => {
    if (!tabActions?.enabled || !name?.trim()) return;
    tabActions.updateTabLabelByKey(
      buildTabKey(location.pathname, location.search),
      `Rule - ${name.trim()}`
    );
  };

  const loadRule = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    const res = await apiRef.current.getById({ apiEntity, id });
    if (res) {
      const loaded = { ...defaultNotificationRule(), ...res };
      if (loaded.triggerConfig?.event === "FIELD_CHANGE") {
        loaded.triggerConfig = { ...loaded.triggerConfig, event: "UPDATE" };
      }
      if (!loaded.channels?.length && loaded.channel) {
        loaded.channels = [loaded.channel];
      }
      setRule(loaded);
      syncTabLabel(res.name || res.code);
    }
    setLoading(false);
  }, [id, isNew]);

  useEffect(() => {
    loadRule();
  }, [loadRule]);

  useEffect(() => {
    if (isNew) return;
    syncTabLabel(rule.name);
  }, [rule.name, isNew, location.pathname, location.search]);

  const patch = (p) => setRule((prev) => ({ ...prev, ...p }));
  const patchTrigger = (p) =>
    setRule((prev) => ({
      ...prev,
      triggerConfig: { ...prev.triggerConfig, ...p },
    }));

  const sanitizeRulePayload = (data) => {
    const payload = { ...data };
    delete payload._id;
    delete payload.code;
    delete payload.createdAt;
    delete payload.updatedAt;
    delete payload.createdBy;

    const watchFields = (payload.triggerConfig?.watchFields ?? [])
      .map((wf) => ({
        field: wf.field?.trim(),
        fromValue: wf.fromValue === "" ? undefined : wf.fromValue,
        toValue: wf.toValue === "" ? undefined : wf.toValue,
      }))
      .filter((wf) => wf.field);

    payload.triggerConfig = {
      ...payload.triggerConfig,
      watchFields,
    };

    payload.recipients = (payload.recipients ?? [])
      .filter((recipient) => recipient.type)
      .map((recipient) => ({
        type: recipient.type,
        documentRole: recipient.documentRole || undefined,
        users: (recipient.users ?? []).map(toRefId).filter(Boolean),
        userRoles: (recipient.userRoles ?? []).map(toRefId).filter(Boolean),
      }));

    const channels = (payload.channels ?? []).filter(Boolean);
    payload.channels = channels;
    if (channels.length) {
      payload.channel = channels[0];
    } else {
      delete payload.channel;
    }

    return payload;
  };

  const handleSave = async () => {
    if (!rule.name?.trim()) {
      notify({
        title: "Validation",
        message: "Rule name is required",
        color: "red",
      });
      setActiveTab("basic");
      return;
    }
    if (!rule.triggerConfig?.targetEntity) {
      notify({
        title: "Validation",
        message: "Target entity is required",
        color: "red",
      });
      setActiveTab("trigger");
      return;
    }
    if (!rule.triggerConfig?.event) {
      notify({
        title: "Validation",
        message: "Event type is required",
        color: "red",
      });
      setActiveTab("trigger");
      return;
    }
    if (!rule.recipients?.length) {
      notify({
        title: "Validation",
        message: "Add at least one recipient group",
        color: "red",
      });
      setActiveTab("recipients");
      return;
    }
    for (const recipient of rule.recipients) {
      if (!recipient.type) {
        notify({
          title: "Validation",
          message: "Each recipient group needs a recipient type",
          color: "red",
        });
        setActiveTab("recipients");
        return;
      }
      if (recipient.type === "DOCUMENT_ROLE" && !recipient.documentRole) {
        notify({
          title: "Validation",
          message: "Select a document role for each document role recipient",
          color: "red",
        });
        setActiveTab("recipients");
        return;
      }
      if (recipient.type === "SPECIFIC_USERS" && !(recipient.users ?? []).length) {
        notify({
          title: "Validation",
          message: "Select at least one user for each specific users recipient",
          color: "red",
        });
        setActiveTab("recipients");
        return;
      }
      if (recipient.type === "USER_ROLE" && !(recipient.userRoles ?? []).length) {
        notify({
          title: "Validation",
          message: "Select at least one user role for each user role recipient",
          color: "red",
        });
        setActiveTab("recipients");
        return;
      }
    }
    const selectedChannels = rule.channels?.length
      ? rule.channels
      : rule.channel
        ? [rule.channel]
        : [];
    if (!selectedChannels.length) {
      notify({
        title: "Validation",
        message: "Select at least one delivery channel",
        color: "red",
      });
      setActiveTab("template");
      return;
    }

    setSaving(true);
    try {
      const payload = sanitizeRulePayload(rule);
      const res = isNew
        ? await apiRef.current.add({ apiEntity, values: payload })
        : await apiRef.current.updateById({ apiEntity, id, values: payload });

      if (!res?.success) {
        throw new Error(getSaveErrorMessage(apiRef.current, res));
      }

      notify({
        title: "Saved",
        message: "Notification rule saved",
        color: "green",
      });

      if (isNew && res.data?._id) {
        navigate(`/management/notificationRules/${res.data._id}`, { replace: true });
      }
    } catch (error) {
      notify({
        title: "Error",
        message: error?.message ?? "Failed to save notification rule",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (isNew) {
      notify({
        title: "Validation",
        message: "Save the rule before testing",
        color: "orange",
      });
      return;
    }
    setTesting(true);
    try {
      const res = await apiRef.current.Notification.testRule(id);
      if (!res) {
        throw new Error("Test request failed. Check your connection and try again.");
      }
      if (!res?.success) {
        throw new Error(getSaveErrorMessage(apiRef.current, res));
      }

      const sent = res.data?.summary?.sent ?? 0;
      const channels = rule.channels?.length
        ? rule.channels
        : rule.channel
          ? [rule.channel]
          : [];
      const channelHint = channels
        .map((ch) => (ch === "IN_APP" ? "in-app inbox" : "email"))
        .join(" and ");

      notify({
        title: "Test sent",
        message:
          sent > 0
            ? `Sent ${sent} test notification(s) to you via ${channelHint || "configured channels"}.`
            : "Test completed.",
        color: "green",
      });
      requestNotificationInboxRefresh();

      const unresolved = res.data?.summary?.unresolvedVariables ?? [];
      if (unresolved.length) {
        notify({
          title: "Unresolved template variables",
          message: `Unresolved: ${unresolved.join(", ")}. Use Insert field tokens from the entity variable list.`,
          color: "orange",
          autoClose: 10000,
        });
      }
    } catch (error) {
      notify({
        title: "Test failed",
        message: error?.message ?? "Could not send test notification",
        color: "red",
      });
    } finally {
      setTesting(false);
    }
  };

  const handleClose = () => {
    if (tabNav.enabled) {
      tabNav.closeActive();
      return;
    }
    navigate("/management/notificationRules");
  };

  if (loading) {
    return (
      <Group position="center" p="xl">
        <Loader />
      </Group>
    );
  }

  return (
    <Page
      title={isNew ? "New Notification Rule" : "Edit Notification Rule"}
      actions={
        <Group>
          <Button variant="default" onClick={handleClose}>
            Close
          </Button>
          {!isNew && (
            <Button color="green" onClick={handleTest} loading={testing}>
              Test
            </Button>
          )}
          <Button onClick={handleSave} loading={saving}>
            Save
          </Button>
        </Group>
      }
    >
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
          <Tabs.Tab value="trigger">Trigger Configuration</Tabs.Tab>
          <Tabs.Tab value="recipients">Recipients</Tabs.Tab>
          <Tabs.Tab value="template">Notification Template</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="basic" pt="xl">
          <Paper shadow="md" p="xl" radius={0}>
            <Stack spacing="md">
              {!isNew && (
                <TextInput label="Code" value={rule.code ?? ""} disabled size="sm" />
              )}
              <TextInput
                label="Name"
                required
                size="sm"
                value={rule.name ?? ""}
                onChange={(e) => patch({ name: e.target.value })}
              />
              <TextInput
                label="Chinese Name"
                size="sm"
                value={rule.cname ?? ""}
                onChange={(e) => patch({ cname: e.target.value })}
              />
              <Textarea
                label="Description"
                size="sm"
                minRows={3}
                value={rule.description ?? ""}
                onChange={(e) => patch({ description: e.target.value })}
              />
              <Switch
                label="Active"
                checked={rule.status === "ACTIVE"}
                onChange={(e) =>
                  patch({ status: e.currentTarget.checked ? "ACTIVE" : "INACTIVE" })
                }
              />
              <NumberInput
                label="Debounce (seconds)"
                description="Minimum seconds between duplicate notifications for the same field change"
                min={0}
                value={rule.debounceSeconds ?? 0}
                onChange={(v) => patch({ debounceSeconds: v ?? 0 })}
              />
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="trigger" pt="xl">
          <FormSectionCard title="Trigger Configuration">
            <Stack spacing="md">
              <Select
                label="Target Entity"
                placeholder="Select entity"
                data={NOTIFICATION_ENTITIES}
                value={rule.triggerConfig?.targetEntity ?? null}
                onChange={(v) => patchTrigger({ targetEntity: v, watchFields: [] })}
                clearable
                required
              />
              <Select
                label="Event Type"
                placeholder="Select event"
                data={NOTIFICATION_EVENTS}
                value={rule.triggerConfig?.event ?? null}
                onChange={(v) => patchTrigger({ event: v })}
                clearable
                required
              />
            </Stack>
          </FormSectionCard>

          {["UPDATE", "FIELD_CHANGE"].includes(rule.triggerConfig?.event) && (
          <FormSectionCard title="Watch Fields">
            <NotificationWatchFieldsEditor
              rows={rule.triggerConfig?.watchFields ?? []}
              onChange={(watchFields) => patchTrigger({ watchFields })}
              entityKey={targetEntity}
            />
          </FormSectionCard>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="recipients" pt="xl">
          <FormSectionCard title="Recipients">
            <Stack spacing="sm">
              <Text size="sm" color="dimmed">
                Add one or more recipient groups. All resolved recipients across groups will
                receive the notification.
              </Text>
              {(rule.recipients ?? []).map((recipient, idx) => (
                <Paper key={idx} p="md" withBorder>
                  <Group position="apart" mb="xs" align="flex-end">
                    <Select
                      label="Recipient Type"
                      placeholder="Select type"
                      data={RECIPIENT_TYPES}
                      value={recipient.type ?? null}
                      style={{ flex: 1 }}
                      required
                      onChange={(v) => {
                        const recipients = [...rule.recipients];
                        recipients[idx] = { type: v };
                        patch({ recipients });
                      }}
                    />
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() =>
                        patch({ recipients: rule.recipients.filter((_, i) => i !== idx) })
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                  {recipient.type === "SPECIFIC_USERS" && (
                    <RemoteSelect2
                      form={{
                        values: { users: recipient.users ?? [] },
                        setFieldValue: (field, val) => {
                          const recipients = [...rule.recipients];
                          recipients[idx] = { ...recipients[idx], users: val };
                          patch({ recipients });
                        },
                        markUserEdited: () => {},
                      }}
                      name="users"
                      label="Users"
                      apiEntity="user"
                      labelRender={formatUserLabel}
                      multiple
                      pageSize={100}
                      maxDropdownHeight={400}
                      searchFields={["name", "staffId", "cname", "email"]}
                      placeholder="Search users"
                    />
                  )}
                  {recipient.type === "DOCUMENT_ROLE" && (
                    <Select
                      label="Document Role"
                      placeholder="Select role"
                      data={docRoles.map((r) => ({ value: r, label: r }))}
                      value={recipient.documentRole ?? null}
                      required
                      disabled={!targetEntity}
                      onChange={(v) => {
                        const recipients = [...rule.recipients];
                        recipients[idx] = { ...recipients[idx], documentRole: v };
                        patch({ recipients });
                      }}
                    />
                  )}
                  {recipient.type === "USER_ROLE" && (
                    <RemoteSelect2
                      form={{
                        values: { userRoles: recipient.userRoles ?? [] },
                        setFieldValue: (field, val) => {
                          const recipients = [...rule.recipients];
                          recipients[idx] = { ...recipients[idx], userRoles: val };
                          patch({ recipients });
                        },
                        markUserEdited: () => {},
                      }}
                      name="userRoles"
                      label="User Roles"
                      apiEntity="userRole"
                      labelField="name"
                      multiple
                      pageSize={100}
                      maxDropdownHeight={400}
                      searchFields={["name"]}
                      placeholder="Search roles"
                    />
                  )}
                </Paper>
              ))}
              <Group>
                <Button
                  leftIcon={<IconPlus size={14} />}
                  onClick={() =>
                    patch({
                      recipients: [...(rule.recipients ?? []), { type: null }],
                    })
                  }
                >
                  Add Recipient Group
                </Button>
              </Group>
            </Stack>
          </FormSectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="template" pt="xl">
          <FormSectionCard title="Notification Template">
            <Stack spacing="md">
              <MultiSelect
                label="Channels"
                placeholder="Select one or more channels"
                data={NOTIFICATION_CHANNELS}
                value={
                  rule.channels?.length
                    ? rule.channels
                    : rule.channel
                      ? [rule.channel]
                      : []
                }
                onChange={(channels) => patch({ channels })}
                required
                clearable
              />
              <Text size="xs" color="dimmed">
                Subject is used as the email subject and in-app notification title. Body is
                used as the email HTML content and in-app message (HTML stripped for in-app).
              </Text>
              <McaNotificationTemplateInput
                label="Subject"
                value={rule.template?.subject ?? ""}
                onChange={(subject) => patch({ template: { ...rule.template, subject } })}
                multiline={false}
                variables={templateVariables}
              />
              <McaNotificationTemplateInput
                label="Body"
                value={rule.template?.body ?? ""}
                onChange={(body) => patch({ template: { ...rule.template, body } })}
                multiline
                minRows={14}
                variables={templateVariables}
              />
              <Text size="xs" color="dimmed">
                {targetEntity
                  ? `Available variables for ${entityLabel(targetEntity)}: ${variables.join(", ")}`
                  : "Select a target entity to see available template variables."}
              </Text>
            </Stack>
          </FormSectionCard>
        </Tabs.Panel>
      </Tabs>
    </Page>
  );
};

export default NotificationRuleEdit;
